import { isNil, uniqBy } from 'lodash-es';
import { queryClient, useAuthUser, type IBaseModel } from '../../api';
import { resourceCatalogQueryKeyFactory } from '../../api/hooks/resourceCatalog/constants';
import { TABLE_SHORTCUTS } from '../../constants/shortcuts';
import { useSafeKeyPress } from '../../hooks/useSafeKeyPress';
import { isInputFocused } from '../../hooks/useSidebarShortcuts';
import entityDrawerStore from '../EntityDrawer/store';
import { useModals } from '../ModalManager';

function getHoveredId() {
	const hoveredId = document.body.getAttribute('table-hovered-data-id');
	return hoveredId;
}

export function useTableShortcuts<T extends IBaseModel>({
	results,
	selectedRecordsState,
	setSelectedRecordsState,
}: {
	results: T[];
	selectedRecordsState: {
		selectedRecords: T[];
		lastSelectedIndex: number | null;
	};
	setSelectedRecordsState: React.Dispatch<
		React.SetStateAction<{
			selectedRecords: T[];
			lastSelectedIndex: number | null;
		}>
	>;
}) {
	const { isViewerOrGuestUser, isEditorOrAdminUser } = useAuthUser();
	const { isModalOpen } = useModals();
	// Condition for shortcuts that require editor/admin permissions and no input focus
	const shouldDisableShortcuts = () =>
		isViewerOrGuestUser || isInputFocused() || isModalOpen();

	useSafeKeyPress(
		TABLE_SHORTCUTS.DESELECT_ALL,
		() => {
			setSelectedRecordsState({ selectedRecords: [], lastSelectedIndex: null });
		},
		() => isModalOpen() // Disable when modal is open
	);

	useSafeKeyPress(
		TABLE_SHORTCUTS.SELECT_ALL,
		(e) => {
			// Only select all if we are hovering over the table.
			if (isNil(getHoveredId())) {
				return;
			}

			e.preventDefault();
			if (selectedRecordsState.selectedRecords.length === results?.length) {
				setSelectedRecordsState({
					selectedRecords: [],
					lastSelectedIndex: null,
				});
			} else {
				setSelectedRecordsState((currentState) => ({
					selectedRecords: results ?? [],
					lastSelectedIndex: currentState.lastSelectedIndex,
				}));
			}
		},
		shouldDisableShortcuts
	);

	useSafeKeyPress(
		TABLE_SHORTCUTS.OPEN_ENTITY_DRAWER,
		(e) => {
			const hoveredId = getHoveredId();
			const hoveredEntity = results?.find((entity) => entity.id === hoveredId);

			// If drawer is already open, close it
			if (entityDrawerStore.isOpen) {
				e.preventDefault();
				entityDrawerStore.closeEntityDrawer();
				return;
			}

			// Otherwise, open it if we have a valid entity
			if (hoveredEntity) {
				const entity = hoveredEntity;
				e.preventDefault();
				if ('entity_type' in entity) {
					entityDrawerStore.openEntityDrawerById(
						isEditorOrAdminUser,
						entity.id,
						undefined,
						() => {
							queryClient.invalidateQueries({
								queryKey: resourceCatalogQueryKeyFactory.allLists(),
							});
						}
					);
				}
			}
		},
		() => isInputFocused() || isModalOpen() // Disable when input is focused or modal is open
	);

	useSafeKeyPress(
		TABLE_SHORTCUTS.TOGGLE_SELECTION,
		(e: KeyboardEvent) => {
			const hoveredId = getHoveredId();
			if (hoveredId) {
				e.preventDefault();
			}

			const hoveredIndex = results?.findIndex(
				(entity) => entity.id === hoveredId
			);
			if (hoveredIndex === -1) {
				return;
			}

			const found = results[hoveredIndex];
			if (found) {
				if (
					selectedRecordsState.selectedRecords.some(
						(record) => record.id === hoveredId
					)
				) {
					setSelectedRecordsState((currentState) => ({
						selectedRecords: currentState.selectedRecords.filter(
							(record) => record.id !== hoveredId
						),
						lastSelectedIndex: hoveredIndex,
					}));
				} else {
					setSelectedRecordsState((currentState) => ({
						selectedRecords: uniqBy(
							[...currentState.selectedRecords, found],
							'id'
						),
						lastSelectedIndex: hoveredIndex,
					}));
				}
			}
		},
		shouldDisableShortcuts
	);
}
